// Helpers et constantes partagées pour les sous-composants de DossierDetail
import { useMemo } from "react";
import {
  User, Calendar, Home, FileText, CheckCircle2, Building2, Clock,
  Wallet, Lock, X, AlertCircle
} from "lucide-react";
import { useStatutsConfig as _useStatutsConfigRaw } from "../../../hooks/useGovernance";
import { formatDateFR, formatDateTimeFR } from "../../../utils/dateFormat";

const API = process.env.REACT_APP_BACKEND_URL;

export const getAuthHeaders = (tenantId, contentType = null) => {
  const token = localStorage.getItem('session_token');
  return {
    "X-Tenant-ID": tenantId || "climatpac",
    ...(contentType && { "Content-Type": contentType }),
    ...(token && { "Authorization": `Bearer ${token}` })
  };
};

// Compat : `formatDate` reste exporté mais renvoie désormais JJ/MM/AAAA
// (cohérent avec le format demandé par Stéphanie). Pour les timestamps
// d'audit (created_at, historique...), utiliser formatDateTimeFR.
export const formatDate = formatDateFR;
export { formatDateFR, formatDateTimeFR };

/**
 * Parse une date stockée en DB qui peut être :
 *  - ISO YYYY-MM-DD (ex: "2026-04-30")
 *  - ISO datetime (ex: "2026-04-30T14:30:00...")
 *  - FR DD/MM/YYYY (ex: "27/06/2025") — legacy importé depuis Google Sheet
 *
 * Retourne un Date (heure 00:00 locale si pas d'heure) ou null si non parsable.
 * Utilisé pour éviter "Invalid Date" sur les 169 dossiers TEF en format FR.
 */
export const parseFRorISO = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const trimmed = dateStr.trim();
  // ISO YYYY-MM-DD ou YYYY-MM-DDTHH:MM...
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const d = new Date(trimmed.length === 10 ? `${trimmed}T00:00` : trimmed);
    return isNaN(d.getTime()) ? null : d;
  }
  // FR DD/MM/YYYY
  const fr = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (fr) {
    const [, dd, mm, yyyy] = fr;
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00`);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export const formatMontant = (montant) => {
  if (!montant && montant !== 0) return "-";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(montant);
};

// Barèmes MPR 2024/2025
const BAREMES_MPR = {
  idf: {
    1: { bleu: 23541, jaune: 28657, violet: 40018 },
    2: { bleu: 34551, jaune: 42058, violet: 58827 },
    3: { bleu: 41493, jaune: 50513, violet: 70382 },
    4: { bleu: 48447, jaune: 58981, violet: 82839 },
    5: { bleu: 55427, jaune: 67473, violet: 94844 },
    supplement: { bleu: 6970, jaune: 8486, violet: 12006 }
  },
  province: {
    1: { bleu: 17009, jaune: 21805, violet: 30549 },
    2: { bleu: 24875, jaune: 31889, violet: 44907 },
    3: { bleu: 29917, jaune: 38349, violet: 54071 },
    4: { bleu: 34948, jaune: 44802, violet: 63235 },
    5: { bleu: 40002, jaune: 51281, violet: 72400 },
    supplement: { bleu: 5045, jaune: 6462, violet: 9165 }
  }
};

export const determinerRegionFiscale = (codePostal) => {
  if (!codePostal || codePostal.length < 2) return null;
  const dept = codePostal.substring(0, 2);
  const deptIDF = ['75', '77', '78', '91', '92', '93', '94', '95'];
  return deptIDF.includes(dept) ? 'idf' : 'province';
};

export function calculerCouleurMPR(rfr, nbPersonnes, region) {
  if (!rfr || !nbPersonnes) return null;
  const bareme = region === "idf" ? BAREMES_MPR.idf : BAREMES_MPR.province;
  let seuils;
  if (nbPersonnes <= 5) {
    seuils = bareme[nbPersonnes];
  } else {
    const base = bareme[5];
    const supplement = bareme.supplement;
    const personnesSupp = nbPersonnes - 5;
    seuils = {
      bleu: base.bleu + (supplement.bleu * personnesSupp),
      jaune: base.jaune + (supplement.jaune * personnesSupp),
      violet: base.violet + (supplement.violet * personnesSupp)
    };
  }
  if (rfr <= seuils.bleu) return { couleur: "bleu", label: "Très modestes", bgClass: "bg-blue-500", textClass: "text-blue-400" };
  if (rfr <= seuils.jaune) return { couleur: "jaune", label: "Modestes", bgClass: "bg-yellow-500", textClass: "text-yellow-400" };
  if (rfr <= seuils.violet) return { couleur: "violet", label: "Intermédiaires", bgClass: "bg-[var(--oh-stat-purple-bg)] border border-[var(--oh-stat-purple-border)]", textClass: "text-[var(--oh-stat-purple-text)]" };
  return { couleur: "rose", label: "Supérieurs", bgClass: "bg-pink-500", textClass: "text-pink-400" };
}

/**
 * etatFactureAMO — LIT l'état calculé par le backend. ⛔ Ne le recalcule plus.
 *
 * 🔑 #569 §A — LA RÈGLE VIT DANS `backend/metier_hub/paiement_facture.py`, EN UN
 * SEUL ENDROIT. Cette fonction se contentait auparavant d'en être le « MIROIR
 * EXACT »… et les deux avaient déjà divergé : le miroir ignorait
 * `pennylane_invoice.status` ET la garde sur les factures annulées, donc un AVOIR
 * y passait pour payé. Un miroir n'est fidèle que le jour où on l'écrit.
 *
 * ⛔ AUCUN REPLI SUR LES CHAMPS BRUTS. Si le backend n'a pas fourni l'état, on
 * rend `null` — « je ne sais pas » — et l'appelant l'affiche comme tel. Un repli
 * local rouvrirait exactement la divergence qu'on vient de fermer, et il le
 * ferait en silence.
 *
 * Rend : "payee" | "non_payee" | "sans_facture" | null (inconnu).
 */
export function etatFactureAMO(dossier) {
  return dossier?.facture_payee_etat ?? null;
}

/**
 * isFacturePayee — ⛔ LEGACY, conservé pour les appelants existants.
 *
 * ⚠️ Rend `false` aussi bien pour « non payée » que pour « pas de facture » et
 * pour « inconnu » : c'est précisément l'aplatissement que #557 puis #569 ont
 * corrigé. Préférer `etatFactureAMO()` partout où la distinction compte.
 */
export function isFacturePayee(dossier) {
  return etatFactureAMO(dossier) === "payee";
}

export const STATUTS_CONFIG = {
  tef: {
    prospect: { label: "Prospect", color: "bg-zinc-500/20 border border-zinc-500/40 text-zinc-700 dark:text-zinc-200", icon: User },
    rdv_pris: { label: "RDV Pris", color: "bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300", icon: Calendar },
    visite_faite: { label: "Visite Faite", color: "bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300", icon: Home },
    dossier_cree: { label: "Dossier créé", color: "bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300", icon: FileText },
    dossier_complet: { label: "Dossier Complet", color: "bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300", icon: FileText },
    devis_signe: { label: "Devis signé", color: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300", icon: CheckCircle2 },
    dal_genere: { label: "DAL Généré", color: "bg-purple-500/15 border border-purple-500/30 text-purple-700 dark:text-purple-300", icon: FileText },
    soumis_anah: { label: "Soumis ANAH", color: "bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300", icon: Building2 },
    dossier_depose: { label: "Déposé", color: "bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300", icon: Building2 },
    en_attente_anah: { label: "En attente ANAH", color: "bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300", icon: Clock },
    aides_en_instruction: { label: "En instruction", color: "bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300", icon: Clock },
    valide_anah: { label: "Validé ANAH", color: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300", icon: CheckCircle2 },
    aides_validees: { label: "Subvention accordée", color: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300", icon: CheckCircle2 },
    refuse_anah: { label: "Refusé ANAH", color: "bg-red-500/15 border border-red-500/30 text-red-700 dark:text-red-300", icon: AlertCircle },
    travaux_en_cours: { label: "Travaux en cours", color: "bg-orange-500/15 border border-orange-500/30 text-orange-700 dark:text-orange-300", icon: Home },
    travaux_termines: { label: "Travaux terminés", color: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300", icon: CheckCircle2 },
    paiement_en_cours: { label: "Paiement en cours", color: "bg-yellow-500/15 border border-yellow-500/30 text-yellow-700 dark:text-yellow-300", icon: Wallet },
    paiement_ok: { label: "Payé", color: "bg-yellow-500/15 border border-yellow-500/30 text-yellow-700 dark:text-yellow-300", icon: Wallet },
    paye: { label: "Soldé", color: "bg-yellow-500/15 border border-yellow-500/30 text-yellow-700 dark:text-yellow-300", icon: Wallet },
    demande_avance: { label: "Demande avance", color: "bg-purple-500/15 border border-purple-500/30 text-purple-700 dark:text-purple-300", icon: Wallet },
    avance_payee: { label: "Avance payée", color: "bg-green-500/15 border border-green-500/30 text-green-700 dark:text-green-300", icon: Wallet },
    demande_solde: { label: "Demande solde", color: "bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-700 dark:text-fuchsia-300", icon: Wallet },
    archive: { label: "Archivé", color: "bg-zinc-500/20 border border-zinc-500/40 text-zinc-700 dark:text-zinc-200", icon: Lock },
    annule: { label: "Annulé", color: "bg-red-500/15 border border-red-500/30 text-red-700 dark:text-red-300", icon: X }
  },
  climat: {
    lead: { label: "Lead", color: "bg-zinc-500/20 border border-zinc-500/40 text-zinc-700 dark:text-zinc-200", icon: User },
    qualifie: { label: "Qualifié", color: "bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300", icon: CheckCircle2 },
    visite_technique: { label: "Visite technique", color: "bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300", icon: Home },
    devis_en_cours: { label: "Devis en cours", color: "bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300", icon: FileText },
    devis_envoye: { label: "Devis envoyé", color: "bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300", icon: FileText },
    devis_signe: { label: "Devis signé", color: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300", icon: CheckCircle2 },
    dossier_aides: { label: "Dossier aides", color: "bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300", icon: FileText },
    travaux_planifies: { label: "Planifié", color: "bg-orange-500/15 border border-orange-500/30 text-orange-700 dark:text-orange-300", icon: Calendar },
    travaux_en_cours: { label: "Travaux", color: "bg-orange-500/15 border border-orange-500/30 text-orange-700 dark:text-orange-300", icon: Home },
    travaux_termines: { label: "Terminé", color: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300", icon: CheckCircle2 },
    facturation: { label: "Facturation", color: "bg-yellow-500/15 border border-yellow-500/30 text-yellow-700 dark:text-yellow-300", icon: Wallet },
    paiement_partiel: { label: "Paiement partiel", color: "bg-yellow-500/15 border border-yellow-500/30 text-yellow-700 dark:text-yellow-300", icon: Wallet },
    paiement_ok: { label: "Payé", color: "bg-yellow-500/15 border border-yellow-500/30 text-yellow-700 dark:text-yellow-300", icon: Wallet },
    archive: { label: "Archivé", color: "bg-zinc-500/20 border border-zinc-500/40 text-zinc-700 dark:text-zinc-200", icon: Lock },
    annule: { label: "Annulé", color: "bg-red-500/15 border border-red-500/30 text-red-700 dark:text-red-300", icon: X }
  }
};

const STATUT_ICON_MAP = {
  prospect: User, rdv_pris: Calendar, visite_faite: Home, dossier_cree: FileText,
  dossier_complet: FileText, devis_signe: CheckCircle2, dal_genere: FileText,
  soumis_anah: Building2, dossier_depose: Building2, en_attente_anah: Clock,
  aides_en_instruction: Clock, valide_anah: CheckCircle2, aides_validees: CheckCircle2,
  refuse_anah: AlertCircle, travaux_en_cours: Home, travaux_termines: CheckCircle2,
  paiement_en_cours: Wallet, paiement_ok: Wallet, paye: Wallet,
  demande_avance: Wallet, avance_payee: Wallet, demande_solde: Wallet,
  archive: Lock, annule: X,
  lead: User, qualifie: CheckCircle2, visite_technique: Home,
  devis_en_cours: FileText, devis_envoye: FileText, dossier_aides: FileText,
  travaux_planifies: Calendar, facturation: Wallet, paiement_partiel: Wallet,
};

export function useStatutsConfig() {
  const { config, loading } = _useStatutsConfigRaw();

  const enriched = useMemo(() => {
    if (!config) return null;
    const enrich = (map) => {
      const out = {};
      for (const [code, info] of Object.entries(map)) {
        out[code] = { ...info, icon: STATUT_ICON_MAP[code] || FileText };
      }
      return out;
    };
    return {
      ...config,
      statuts: {
        tef: enrich(config.statuts?.tef || {}),
        climat: enrich(config.statuts?.climat || {})
      }
    };
  }, [config]);

  return {
    config: enriched || { statuts: STATUTS_CONFIG, phase_statuts: {}, phase_labels: {} },
    loading
  };
}

export { API };

/**
 * Ouvre un PDF (récupéré via fetch authentifié) dans un nouvel onglet SANS être
 * bloqué par le pop-up blocker.
 *
 * Cause du bug corrigé : appeler `window.open(url)` APRÈS un `await fetch(...)`
 * n'est plus considéré par le navigateur comme déclenché par le clic
 * utilisateur → l'onglet est bloqué. Ici on ouvre l'onglet SYNCHRONE (dans le
 * geste utilisateur, avant tout await), puis on y pointe le blob une fois
 * chargé. Source unique réutilisée par tous les écrans "Voir"/"PDF".
 *
 * @returns {Promise<boolean>} true si le PDF a pu être ouvert.
 */
export async function openAuthedPdf({ url, headers, credentials = "include", onError } = {}) {
  // Ouverture SYNCHRONE dans le geste utilisateur (sinon pop-up bloqué après await).
  const win = window.open("", "_blank");
  try {
    const res = await fetch(url, { headers, credentials });
    if (!res.ok) {
      if (win) win.close();
      const err = await res.json().catch(() => ({}));
      onError?.(err.detail || `Erreur ${res.status}`);
      return false;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    if (win) {
      win.location = blobUrl;
    } else {
      // Fallback si l'onglet a quand même été bloqué (navigateur strict).
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    return true;
  } catch (e) {
    if (win) win.close();
    onError?.(e?.message || "Erreur réseau");
    return false;
  }
}
